import { isValid } from "date-fns";
import { z } from "zod";

import { tiptapDefaultJson } from "@/lib/constants";
import { JobDifficulty, JobUrgency } from "@/types";

const allowedDocumentLogoMimeTypes = ["image/png", "image/jpeg", "image/jpg"];
const allowedResumeFileMimeTypes = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];

const hasProperDateandYear = (val: string) => {
  if (!val) return true;
  // Require a 4-digit year in the 2000s, so we'll have to remember to update Dec 31st 2099 :)
  const hasFourDigitYear = /\b(20)\d{2}\b/.test(val);
  // Require a day component (1-31) - check for numeric day or numeric date formats
  const hasDay =
    /\b([1-9]|[12]\d|3[01])\b/.test(val) ||
    /^\d{1,2}[/-]\d{1,2}[/-]/.test(val.trim());
  return hasFourDigitYear && hasDay;
};

const dateErrorMessage =
  "Date must include a day, month, and full 4-digit year (e.g., 1/1/2025, Jan 1 2025)";

export const nullableDateSchema = z
  .string()
  .nullable()
  .refine(
    (val) => {
      if (!val) return true;

      if (!hasProperDateandYear(val)) return false;

      return isValid(new Date(val));
    },
    {
      message: dateErrorMessage,
    },
  );

export const nullableNonOptionalNumberSchema = z
  .union([z.string(), z.number()])
  .transform((val) => {
    if (typeof val === "string" && val !== "") return Number(val);
    if (val === "") return null;
    return val;
  })
  .nullable();

export const nullableNumberSchema = z
  .union([z.string(), z.number()])
  .transform((val) => {
    if (typeof val === "string" && val !== "") return Number(val);
    if (val === "") return null;
    return val;
  })
  .nullable()
  .optional();

export const requiredDateSchema = z.string().refine(
  (val) => {
    if (!val) return false;

    if (!hasProperDateandYear(val)) return false;

    return isValid(new Date(val));
  },
  {
    message: dateErrorMessage,
  },
);

export const requiredNumberSchema = z
  .union([z.string(), z.number()])
  .transform((val) => {
    if (typeof val === "string") return Number(val);
    return val;
  })
  .pipe(z.number().min(1, "Must be greater than 0"));

// Whole-number count (1 or higher). Coerces string inputs from text fields.
export const positiveIntegerSchema = z.coerce
  .number()
  .int()
  .min(1, "Must be greater than 0");

export const resumeFileSchema = z
  .instanceof(File)
  .refine((file) => allowedResumeFileMimeTypes.includes(file.type), {
    message: "File must be .pdf, .docx, or .doc",
  });
export const optionalResumeFileSchema = resumeFileSchema.optional();

export const documentLogoSchema = z
  .instanceof(File)
  .refine((file) => allowedDocumentLogoMimeTypes.includes(file.type), {
    message: "File must be a PNG, JPEG, JPG",
  });
export const optionalDocumentLogoSchema = documentLogoSchema.optional();

export const difficultyUrgencySchema = z.object({
  difficulty: z.nativeEnum(JobDifficulty, {
    required_error: "A difficulty selection is required",
  }),
  urgency: z.nativeEnum(JobUrgency, {
    required_error: "An urgency selection is required",
  }),
});

// A tiptap document that isn't empty.
export const tiptapContentJsonSchema = z
  .object({
    type: z.literal("doc"),
    content: z.array(z.any()),
  })
  .refine(
    (data) =>
      JSON.stringify(data.content) !==
      JSON.stringify(tiptapDefaultJson.content),
    { message: "Required" },
  );
